import { Product } from "features/ProductReel/types";
import React from "react";
import styled from "styled-components/macro";
import ColdInfo from "assets/images/ProductDetails/cold_info.svg";
import HotInfo from "assets/images/ProductDetails/hot_info.svg";
import { device, toRem } from "theme/Breakpoints";
import { colors } from "theme/Theme";
import { DefaultText } from "theme/GlobalElements";

interface HotColdDescriptionProps {
  fetchedProduct: Product;
}

const HotColdDescription: React.FC<HotColdDescriptionProps> = ({
  fetchedProduct,
}) => {
  const getCoolDescription = (product: Product) => {
    if (!product) return [];
    return [
      {
        title: "Енергиен клас",
        value: product?.description.energy_class_cooling,
      },
      {
        title: "SEER",
        value: product?.tech_specs?.data?.common_specs.data.seer.value,
      },
      {
        title: "Отдава (kW)",
        value: product?.description.outbound_power_cooling,
      },
      {
        title: "Консумирa (kW)",
        value: product?.description.en_consumption_cooling,
      },
    ].map((item: any) => {
      return (
        <InfoItem key={item.title}>
          <InfoTitle className="text-sm">{item.title}</InfoTitle>
          <Info className="text-body">{item.value}</Info>
        </InfoItem>
      );
    });
  };

  const getHotDescription = (product: Product) => {
    if (!product) return [];
    return [
      {
        title: "Енергиен клас",
        value: product?.description.energy_class_heating,
      },
      {
        title: "SCOP",
        value: product?.tech_specs?.data?.common_specs.data.scop.value,
      },
      {
        title: "Отдава (kW)",
        value: product?.description.outbound_power_heating,
      },
      {
        title: "Консумирa (kW)",
        value: product?.description.en_consumption_heating,
      },
    ].map((item: any, idx: number) => {
      return (
        <InfoItem key={idx}>
          <InfoTitle className="text-sm">{item.title}</InfoTitle>
          <Info className="text-body">{item.value}</Info>
        </InfoItem>
      );
    });
  };

  return (
    <HotColdDescriptionContainer>
      <HotDescription className="item">
        <TopSection>
          <SVGImg src={ColdInfo} />
          <DescriptionTitle className="cold text-md">
            Режим на охлаждане
          </DescriptionTitle>
        </TopSection>
        <InfoItems>{getCoolDescription(fetchedProduct)}</InfoItems>
      </HotDescription>
      <ColdDescription className="item">
        <TopSection>
          <SVGImg src={HotInfo} />
          <DescriptionTitle className="hot text-md">
            Режим на отопление
          </DescriptionTitle>
        </TopSection>
        <InfoItems>{getHotDescription(fetchedProduct)}</InfoItems>
      </ColdDescription>
    </HotColdDescriptionContainer>
  );
};

const HotColdDescriptionContainer = styled.div`
  display: flex;
  flex-flow: column;
  padding: 0 40px;
  flex: 1;

  .item + .item {
    margin-top: 20px;
  }

  @media only screen and (${device.tablet}) {
    flex-flow: row nowrap;
    justify-content: space-between;
    margin-top: 20px;
    padding: 0 10px;

    .item {
      flex: 0 49%;
    }

    .item + .item {
      margin-top: 0;
    }
  }

  @media only screen and (${device.laptop}) {
    padding: 0;
  }
`;

const HotDescription = styled.div`
  flex: 1;
`;
HotDescription.displayName = "HotDescription";

const ColdDescription = styled.div`
  flex: 1;
`;
ColdDescription.displayName = "ColdDescription";

const TopSection = styled.div`
  flex: 1;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;

  @media only screen and (${device.tablet}) {
    justify-content: center;
  }
  @media only screen and (${device.laptopL}) {
    justify-content: flex-start;
  }
`;
TopSection.displayName = "TopSection";

const SVGImg = styled.img`
  width: 48px;
  height: 48px;
  margin-right: 20px;
  @media only screen and (${device.tablet}) {
    margin-right: 30px;
    width: 48px;
    height: 48px;
  }
`;
SVGImg.displayName = "SVGImg";

const DescriptionTitle = styled(DefaultText)`
  font-weight: 500;

  &.hot {
    color: ${colors.red};
  }

  &.cold {
    color: ${colors.lighter_blue};
  }

  &.cold,
  &.hot {
    font-weight: 500;
  }
`;
DescriptionTitle.displayName = "DescriptionTitle";

const InfoItems = styled.div`
  margin-top: 20px;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
`;
InfoItems.displayName = "InfoItems";

const InfoItem = styled.div`
  flex: 1 49%;
  display: flex;
  flex-flow: column;
  align-items: center;
  &:nth-child(3),
  &:nth-child(4) {
    margin-top: 10px;
  }

  @media only screen and (${device.tablet}) {
    &:nth-child(3),
    &:nth-child(4) {
      margin-top: 20px;
    }
  }

  @media only screen and (${device.laptopL}) {
    align-items: flex-start;
  }
`;
InfoItem.displayName = "InfoItem";

const InfoTitle = styled(DefaultText)`
  color: ${colors.darkest_gray};
`;
InfoTitle.displayName = "InfoTitle";

const Info = styled(DefaultText)`
  font-weight: bold;
`;
Info.displayName = "Info";

export default HotColdDescription;
